/**
 * Evaluador de dominios del lado del cliente.
 *
 * Sirve para UNA cosa: las decoraciones de la lista (`{"danger": [...]}`), que
 * se aplican sobre filas ya cargadas. No es — y no puede ser — un sustituto del
 * filtrado del backend: acá no hay base de datos, no hay reglas de fila y no hay
 * relaciones que atravesar.
 *
 * Por eso el criterio ante lo que no se puede resolver es **falso**: un dominio
 * que menciona un campo que la fila no trajo no pinta nada. Lo contrario haría
 * que una fila se vea en rojo por un dato que el usuario no está viendo.
 *
 * La gramática es la misma que la del backend (`core/domain.py`): notación
 * polaca prefija, `&` y `|` binarios, `!` unario, y AND implícito entre las
 * expresiones que quedan sueltas en el nivel superior.
 *
 * **Los decimales llegan como string.** `_to_json` los serializa así para no
 * perder precisión, así que `"0.000000" === 0` es falso y `"99.00" > "100"` es
 * verdadero por orden lexicográfico. Comparar con `===` haría que TODA
 * decoración sobre un importe dé el resultado equivocado — «marcá las unidades
 * con coeficiente cero» no marcaría ninguna y «marcá los ajustes distintos de
 * cero» las marcaría todas. Por eso se compara POR TIPO: numérico contra
 * numérico (ver {@link parNumerico}).
 */

import { isMany2One } from '@/components/fields/format';
import type { Domain, DomainLeaf, DomainNode, FieldMeta, FieldMetaMap, JsonValue, RecordData } from '@/lib/types';

const CONECTORES = new Set(['&', '|', '!']);

/** Widgets cuyo valor es un número aunque el tipo de Django diga otra cosa. */
const WIDGETS_NUMERICOS = new Set(['money', 'monetary', 'percent', 'progress', 'area']);

function esHoja(token: DomainNode): token is DomainLeaf {
  return Array.isArray(token) && token.length === 3;
}

/**
 * Valor comparable de un campo de la fila.
 *
 * Un many2one se compara por id, que es lo que hace el backend (`Q(campo=valor)`
 * sobre una FK compara la clave). Las rutas relacionales (`a.b`) no se pueden
 * resolver con una fila plana: devuelven `undefined` y el término da falso.
 */
function valorDe(registro: RecordData, campo: string): JsonValue | undefined {
  const raiz = campo.split(/[.]|__/)[0];
  if (raiz === undefined) return undefined;
  if (raiz !== campo) return undefined;

  const bruto = registro[campo];
  if (bruto === undefined) return undefined;
  if (isMany2One(bruto)) return bruto.id;
  if (Array.isArray(bruto)) return bruto as JsonValue;
  return bruto as JsonValue;
}

/** `true` si la metadata dice que el campo es numérico. */
function metaEsNumerica(meta: FieldMeta | undefined): boolean {
  if (meta === undefined) return false;
  if (meta.type === 'integer' || meta.type === 'float' || meta.type === 'decimal') return true;
  return meta.widget !== undefined && WIDGETS_NUMERICOS.has(meta.widget);
}

/** Número de un valor que puede venir como `number` o como string decimal. */
function aNumero(valor: JsonValue | undefined): number | null {
  if (typeof valor === 'number') return Number.isFinite(valor) ? valor : null;
  if (typeof valor === 'string') {
    const limpio = valor.trim();
    // Regex estricta a propósito: `Number("")` es 0 y `Number(" 12 ")` es 12,
    // y ninguno de los dos es un decimal serializado por el backend.
    if (!/^[-+]?\d+(\.\d+)?([eE][-+]?\d+)?$/.test(limpio)) return null;
    const numero = Number(limpio);
    return Number.isFinite(numero) ? numero : null;
  }
  return null;
}

/**
 * Los dos lados como números, o `null` si no corresponde compararlos así.
 *
 * Con metadata del campo alcanza con que sea numérico: los dos lados pueden ser
 * strings (`"0.00"` contra `"0"`) y se comparan bien igual. Sin metadata se
 * exige que al menos uno sea un `number` de verdad, porque `"007"` contra
 * `"7"` son dos códigos distintos en un campo de texto y convertirlos los haría
 * iguales.
 */
function parNumerico(
  izquierda: JsonValue | undefined,
  derecha: JsonValue,
  meta: FieldMeta | undefined,
): [number, number] | null {
  if (typeof izquierda === 'boolean' || typeof derecha === 'boolean') return null;
  if (!metaEsNumerica(meta) && typeof izquierda !== 'number' && typeof derecha !== 'number') return null;

  const a = aNumero(izquierda);
  const b = aNumero(derecha);
  if (a === null || b === null) return null;
  return [a, b];
}

/** Traduce un patrón SQL (`%`, `_`) a una expresión regular anclada y escapada. */
function patronALaRegex(patron: string, sensible: boolean): RegExp {
  let salida = '';
  for (const caracter of patron) {
    if (caracter === '%') salida += '.*';
    else if (caracter === '_') salida += '.';
    else salida += caracter.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
  return new RegExp(`^${salida}$`, sensible ? '' : 'i');
}

/** Igualdad de un valor de la fila contra un literal del dominio. */
function sonIguales(izquierda: JsonValue | undefined, derecha: JsonValue, meta: FieldMeta | undefined): boolean {
  const numeros = parNumerico(izquierda, derecha, meta);
  if (numeros !== null) return numeros[0] === numeros[1];
  return izquierda === derecha;
}

function comparar(
  izquierda: JsonValue | undefined,
  operador: string,
  derecha: JsonValue,
  meta: FieldMeta | undefined,
): boolean {
  if (operador === '=') {
    if (derecha === null) return izquierda === null || izquierda === undefined;
    return sonIguales(izquierda, derecha, meta);
  }
  if (operador === '!=') {
    if (derecha === null) return izquierda !== null && izquierda !== undefined;
    return !sonIguales(izquierda, derecha, meta);
  }

  if (operador === 'in' || operador === 'not in') {
    const lista = Array.isArray(derecha) ? derecha : [derecha];
    const contenido = Array.isArray(izquierda)
      ? izquierda.some((elemento) => lista.some((candidato) => sonIguales(elemento, candidato, meta)))
      : lista.some((candidato) => sonIguales(izquierda, candidato, meta));
    return operador === 'in' ? contenido : !contenido;
  }

  if (izquierda === undefined || izquierda === null) return false;

  if (operador === '>' || operador === '>=' || operador === '<' || operador === '<=') {
    // Numeros como numeros; el resto (fechas ISO, texto) por orden lexicografico,
    // que para ISO 8601 coincide con el cronologico.
    const numeros = parNumerico(izquierda, derecha, meta);
    const a = typeof izquierda === 'boolean' ? Number(izquierda) : izquierda;
    const b = typeof derecha === 'boolean' ? Number(derecha) : derecha;
    const x = numeros === null ? String(a) : numeros[0];
    const y = numeros === null ? String(b) : numeros[1];
    if (operador === '>') return x > y;
    if (operador === '>=') return x >= y;
    if (operador === '<') return x < y;
    return x <= y;
  }

  const texto = String(izquierda);
  const buscado = String(derecha);

  if (operador === 'like') return texto.includes(buscado);
  if (operador === 'ilike') return texto.toLowerCase().includes(buscado.toLowerCase());
  if (operador === 'not ilike') return !texto.toLowerCase().includes(buscado.toLowerCase());
  if (operador === '=like') return patronALaRegex(buscado, true).test(texto);

  // `child_of` necesita recorrer la jerarquia en la base: no es evaluable acá.
  return false;
}

function evaluarHoja(hoja: DomainLeaf, registro: RecordData, fields: FieldMetaMap | undefined): boolean {
  const [campo, operador, valor] = hoja;
  if (typeof campo !== 'string' || typeof operador !== 'string') return false;
  return comparar(valorDe(registro, campo), operador, valor, fields?.[campo]);
}

/** Consume UNA expresión desde `pos`. Devuelve el resultado y la próxima posición. */
function analizar(
  tokens: readonly DomainNode[],
  pos: number,
  registro: RecordData,
  fields: FieldMetaMap | undefined,
): [boolean, number] {
  const token = tokens[pos];
  if (token === undefined) return [true, pos];

  if (typeof token === 'string' && CONECTORES.has(token)) {
    if (token === '!') {
      const [operando, siguiente] = analizar(tokens, pos + 1, registro, fields);
      return [!operando, siguiente];
    }
    const [izquierda, trasIzquierda] = analizar(tokens, pos + 1, registro, fields);
    const [derecha, trasDerecha] = analizar(tokens, trasIzquierda, registro, fields);
    return [token === '&' ? izquierda && derecha : izquierda || derecha, trasDerecha];
  }

  if (!esHoja(token)) return [false, pos + 1];
  return [evaluarHoja(token, registro, fields), pos + 1];
}

/**
 * `true` si el registro satisface el dominio. Un dominio vacío siempre da `true`.
 *
 * @param fields metadata del modelo. Es opcional para no atar el evaluador a una
 *   pantalla, pero conviene pasarla: es lo que permite saber que un `"0.00"` es
 *   un decimal y no un texto, y comparar en consecuencia.
 */
export function evaluateDomain(
  domain: Domain | undefined,
  registro: RecordData,
  fields?: FieldMetaMap,
): boolean {
  if (domain === undefined || domain.length === 0) return true;

  let resultado = true;
  let pos = 0;
  while (pos < domain.length) {
    const [expresion, siguiente] = analizar(domain, pos, registro, fields);
    if (siguiente === pos) break;
    resultado = resultado && expresion;
    pos = siguiente;
  }
  return resultado;
}
